// filepath: \\wsl.localhost\Ubuntu\home\simeds\sisketron\frontend\src\components\Tabelle\TabelleCustomActions.jsx
import React, { useState } from 'react';
import { FaCogs, FaSpinner, FaEye, FaTimes } from 'react-icons/fa';
import api from '../../config/axios';
import { useTableContext } from '../data/TableManager/context/TableContext';
import { useTheme } from '../../contexts/ThemeContext';
import { logger } from '../../utils/logger';
import CensimentoDifferencesModal from '../common/CensimentoDifferencesModal';

const TabelleCustomActions = () => {
  const { selectedRow, setError, setSuccess } = useTableContext();
  const { theme: currentThemeName } = useTheme();
  const safeTheme = React.useMemo(() => ({ themeColor: currentThemeName === 'dark' ? 'indigo' : 'emerald' }), [currentThemeName]);
  const themeColor = safeTheme.themeColor;

  const selectedTableId = selectedRow?.TAB_Id_Tabelle;

  const [isGenerating, setIsGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [pendingDifferences, setPendingDifferences] = useState([]);
  const [pendingTableName, setPendingTableName] = useState('');
  const [pendingPayload, setPendingPayload] = useState(null);

  const handleGenerateItems = async () => {
    if (!selectedTableId) {
      setError("Nessuna tabella selezionata.");
      return;
    }
    const generaFiltro = !!selectedRow?.TAB_GeneraFiltro; 

    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    setGenerationResult(null);

    try {
      // STEP 1: Dry run — esegui censimento in transazione, rileva differenze, rollback
      const payload = {
        idTabella: selectedTableId,
        filtro: generaFiltro,
        dryRun: true,
      };
      logger.info('[TabelleCustomActions] DryRun payload:', payload);
      const dryRunResponse = await api.post('/censimento/oggetto-da-tabella', payload);
      logger.info('[TabelleCustomActions] DryRun risposta:', dryRunResponse.data);

      if (dryRunResponse.data?.success && dryRunResponse.data.dryRun && dryRunResponse.data.hasDifferences) {
        // Ci sono differenze: mostra modale
        setPendingDifferences(dryRunResponse.data.differences);
        setPendingTableName(dryRunResponse.data.tableName || '');
        setPendingPayload({ idTabella: selectedTableId, filtro: generaFiltro });
        setShowDiffModal(true);
        setIsGenerating(false);
        return;
      }

      // Nessuna differenza (o primo censimento): procedi direttamente
      await executeCensimento({ idTabella: selectedTableId, filtro: generaFiltro });
    } catch (err) {
      logger.error("[TabelleCustomActions] Errore API Censimento Item:", err);
      setError(err.response?.data?.message || err.message || "Errore di comunicazione con il server durante il censimento.");
      setIsGenerating(false);
    }
  };

  // Esegue il censimento effettivo (con o senza preserveMap)
  const executeCensimento = async (basePayload, preserveMap = null) => {
    setIsGenerating(true);
    setShowDiffModal(false);
    try {
      const payload = {
        ...basePayload,
        dryRun: false,
        preserveMap: preserveMap,
      };
      logger.info('[TabelleCustomActions] Payload censimento definitivo:', payload);
      const response = await api.post('/censimento/oggetto-da-tabella', payload);
      logger.info('[TabelleCustomActions] Risposta censimento:', response.data);

      if (response.data?.success) {
        setSuccess(response.data.message || "Censimento automatico Item completato con successo.");
        setGenerationResult({
          type: 'table',
          data: response.data,
          timestamp: new Date().toISOString()
        });
      } else {
        setError(response.data.message || "Errore durante il censimento automatico Item.");
      }
    } catch (err) {
      logger.error("[TabelleCustomActions] Errore API Censimento Item:", err);
      setError(err.response?.data?.message || err.message || "Errore di comunicazione con il server durante il censimento.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Callback modale differenze: conferma con preservazione
  const handleDiffConfirm = (preserveMap) => {
    if (pendingPayload) {
      executeCensimento(pendingPayload, preserveMap);
    }
  };

  // Callback modale differenze: annulla
  const handleDiffCancel = () => {
    setShowDiffModal(false);
    setPendingDifferences([]);
    setPendingTableName('');
    setPendingPayload(null);
    setIsGenerating(false);
  };

  const handleShowResults = () => {
    if (generationResult) {
      setShowResultModal(true);
    }
  };

  const renderResultDetails = () => {
    if (!generationResult || generationResult.type !== 'table') return null;
    const { data } = generationResult;
    if (!data) return <p>Nessun dato dettagliato disponibile.</p>;

    return (
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900 dark:text-gray-100">Oggetti e Attributi Generati</h4>
        {data.mainObject && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Oggetto Principale {data.mainObject.operation && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded ml-2">
                  {data.mainObject.operation === 'create' ? 'Creato' : 'Aggiornato'}
                </span>
              )}
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><strong>ID:</strong> {data.mainObject.objectId}</div>
              <div><strong>Nome:</strong> {data.mainObject.objectName}</div>
              <div><strong>Attributi Creati:</strong> {data.mainObject.attributesCount}</div>
            </div>
            {data.mainObject.createdAttributes && data.mainObject.createdAttributes.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Dettaglio Attributi ({data.mainObject.createdAttributes.length})</summary>
                <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                  {data.mainObject.createdAttributes.map((attr, idx) => (
                    <li key={idx}>
                      {attr.name} (ID: {attr.id}, Tipo: {attr.fieldType || attr.type}, Sorgente: {attr.sourceField || 'N/A'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {data.filterObject && (
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">
              Oggetto Filtro {data.filterObject.operation && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded ml-2">
                  {data.filterObject.operation === 'create' ? 'Creato' : 'Aggiornato'}
                </span>
              )}
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div><strong>ID:</strong> {data.filterObject.objectId}</div>
              <div><strong>Nome:</strong> {data.filterObject.objectName}</div>
              <div><strong>Attributi Creati:</strong> {data.filterObject.attributesCount}</div>
            </div>
             {data.filterObject.createdAttributes && data.filterObject.createdAttributes.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer">Dettaglio Attributi ({data.filterObject.createdAttributes.length})</summary>
                <ul className="list-disc pl-5 mt-1 max-h-48 overflow-y-auto">
                  {data.filterObject.createdAttributes.map((attr, idx) => (
                    <li key={idx}>
                      {attr.name} (ID: {attr.id}, Tipo: {attr.fieldType || attr.type}, Sorgente: {attr.sourceField || 'N/A'})
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    );
  };

  const buttonClass = `px-3 py-2 text-sm bg-${themeColor}-600 text-white rounded hover:bg-${themeColor}-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center`;
  const iconClass = "mr-1.5 h-4 w-4";

  return (
    <>
      <div className="my-4 p-4 bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">Azioni Specifiche Tabella</h3>
        {selectedTableId ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={handleGenerateItems} 
                className={buttonClass} 
                disabled={!selectedTableId || isGenerating}
              >
                {isGenerating ? <FaSpinner className={`${iconClass} animate-spin`} /> : <FaCogs className={iconClass} />}
                {isGenerating ? 'Censimento in corso...' : 'Censimento automatico Item'}
              </button>
            </div>
            {generationResult && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                <button 
                  onClick={handleShowResults} 
                  className={`px-3 py-2 text-sm bg-${themeColor}-500 text-white rounded hover:bg-${themeColor}-600 transition flex items-center justify-center`}
                >
                  <FaEye className={iconClass} /> Visualizza Risultati Censimento
                </button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Seleziona una tabella per abilitare le azioni.</p>
        )}
      </div>

      {showResultModal && generationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className={`flex justify-between items-center px-6 py-4 bg-${themeColor}-100 dark:bg-${themeColor}-900 border-b border-gray-300 dark:border-gray-700`}>
              <h3 className={`text-lg font-semibold text-${themeColor}-800 dark:text-${themeColor}-100`}>
                Risultati Censimento Automatico Item
              </h3>
              <button 
                onClick={() => setShowResultModal(false)}
                className={`text-${themeColor}-600 hover:text-${themeColor}-800 dark:text-${themeColor}-400 dark:hover:text-${themeColor}-200`}
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                <strong>Timestamp:</strong> {new Date(generationResult.timestamp).toLocaleString()}
              </div>
              {renderResultDetails()}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">Dati Raw (Debug)</h4>
                <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-xs overflow-x-auto border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200">
                  {JSON.stringify(generationResult.data, null, 2)}
                </pre>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button 
                onClick={() => setShowResultModal(false)}
                className={`px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale differenze censimento */}
      <CensimentoDifferencesModal
        isOpen={showDiffModal}
        differences={pendingDifferences}
        tableName={pendingTableName}
        onConfirm={handleDiffConfirm}
        onCancel={handleDiffCancel}
        themeColor={themeColor}
      />
    </>
  );
};

export default TabelleCustomActions;